/**
 * Platform-agnostic toast sink for the sync client.
 *
 * Shared code (persistence errors, op-rejected) needs to surface a toast, but
 * it must NOT reach for a platform toast library: `sonner` on web renders its
 * OWN built-in card — red slab, filled icon, circular close button — which is
 * not the design-system `<Toast>` and bypasses every DS token. That is exactly
 * the bug this module exists to prevent; mobile had already worked around it
 * with a Metro alias + `<SonnerToastBridge />`, web had nothing.
 *
 * So: shared emits through this sink and each platform registers its own DS
 * toast at boot —
 *   - web    → `flashka-toast` (the sonner shim that renders `<Toast>`), see App.tsx
 *   - mobile → `emitToast` via `<SonnerToastBridge />`
 *
 * The default sink is a no-op so a module-load emit (or one before the platform
 * has registered) can never crash.
 */

/** Maps to the DS Toast variants shared code can express. */
export type SharedToastVariant = 'critical' | 'success' | 'default';

/**
 * Platform-agnostic leading-icon token. Shared code must stay icon-lib-free
 * (lucide-react on web, lucide-react-native on mobile), so it names the glyph
 * and each sink maps it — same trick as the leaderboard's `ToastBadgeIcon`.
 */
export type SharedToastIcon = 'alert' | 'info';

export type SharedToastPayload = {
  /** DS Toast title — the outcome. */
  title: string;
  /** DS Toast description — the "so what?" line. */
  description?: string;
  /** Auto-dismiss in ms. Persistence errors hold longer than the 4s default. */
  duration?: number;
  /** Stable id, so a repeated emit replaces the toast instead of stacking. */
  id?: string;
  /** Leading status glyph, resolved to a real icon by the platform sink. */
  icon?: SharedToastIcon;
};

export type SharedToastSink = (
  variant: SharedToastVariant,
  payload: SharedToastPayload,
) => void;

const NOOP: SharedToastSink = () => {};

let sink: SharedToastSink = NOOP;

/**
 * Register the platform's DS-toast renderer. Call once at app boot (web) or
 * from an effect inside the provider tree (mobile, which needs the live
 * context). Pass `null` to revert to the no-op on teardown.
 */
export function setSharedToastSink(next: SharedToastSink | null): void {
  sink = next ?? NOOP;
}

/** Emit a toast from shared code. No-op until a platform registers a sink. */
export function emitSharedToast(
  variant: SharedToastVariant,
  payload: SharedToastPayload,
): void {
  sink(variant, payload);
}
